import type { FindingFilters, RiskState, Severity } from "../types";
import { EMPTY_TAG_FILTER, normalizeTagFilter, type TagFilterValue } from "./tagFilter";

const SEVERITY_ORDER: Severity[] = ["Critical", "High", "Medium", "Low", "Info"];
const RISK_ORDER: RiskState[] = ["risk_accepted", "under_review", "mitigated"];

export type FindingsFilterUrlState = {
  projectId?: number;
  pipelineId?: string;
  title: string;
  createdFrom: string;
  createdTo: string;
  statusUpdatedFrom: string;
  statusUpdatedTo: string;
  mitigatedFrom: string;
  mitigatedTo: string;
  workItemLinkedFrom: string;
  workItemLinkedTo: string;
  projectVersion: string;
  file: string;
  cwe: string;
  severities: Severity[];
  tags: TagFilterValue;
  status: "All" | "Active" | "Non-Active";
  risk: RiskState[];
  aiStatus: string;
  workItemStatus: "all" | "none" | "any" | "OPEN" | "IN_PROGRESS" | "DONE" | "CANCELLED" | "UNKNOWN";
};

export type FindingStatusFilter = FindingsFilterUrlState["status"];

export const DEFAULT_FINDINGS_FILTERS: FindingsFilterUrlState = {
  projectId: undefined,
  pipelineId: undefined,
  title: "",
  createdFrom: "",
  createdTo: "",
  statusUpdatedFrom: "",
  statusUpdatedTo: "",
  mitigatedFrom: "",
  mitigatedTo: "",
  workItemLinkedFrom: "",
  workItemLinkedTo: "",
  projectVersion: "",
  file: "",
  cwe: "",
  severities: [],
  tags: EMPTY_TAG_FILTER,
  status: "All",
  risk: [],
  aiStatus: "All",
  workItemStatus: "all",
};

const FINDING_STATUS_VALUES = new Set<FindingStatusFilter>(["All", "Active", "Non-Active"]);

export function toFindingStatusFilter(value: string): FindingStatusFilter {
  if (FINDING_STATUS_VALUES.has(value as FindingStatusFilter)) {
    return value as FindingStatusFilter;
  }
  return "All";
}

function parseCsv(raw: string | null): string[] {
  return (raw ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function orderByPriority<T extends string>(items: T[], order: readonly T[]): T[] {
  const rank = new Map(order.map((item, index) => [item, index]));
  return [...new Set(items)].sort((left, right) => {
    const leftRank = rank.get(left) ?? Number.MAX_SAFE_INTEGER;
    const rightRank = rank.get(right) ?? Number.MAX_SAFE_INTEGER;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return left.localeCompare(right);
  });
}

const WORK_ITEM_STATUS_VALUES = new Set(["all", "none", "any", "OPEN", "IN_PROGRESS", "DONE", "CANCELLED", "UNKNOWN"]);

function _parseWorkItemStatus(params: URLSearchParams): FindingsFilterUrlState["workItemStatus"] {
  const raw = params.get("work_item_status");
  if (raw && WORK_ITEM_STATUS_VALUES.has(raw)) {
    return raw as FindingsFilterUrlState["workItemStatus"];
  }
  // backwards-compat: old has_work_item param
  const legacy = params.get("has_work_item");
  if (legacy === "yes" || legacy === "true") return "any";
  if (legacy === "no" || legacy === "false") return "none";
  return "all";
}

// `tags` (any of) and `tags__and` (all of) mirror the API one to one; a link
// carrying both is read as all-of because that is the stricter request.
function parseTagFilter(params: URLSearchParams): TagFilterValue {
  const allOf = parseCsv(params.get("tags__and"));
  const anyOf = parseCsv(params.get("tags"));
  return normalizeTagFilter({
    include: allOf.length ? allOf : anyOf,
    exclude: parseCsv(params.get("not_tags")),
    matchMode: allOf.length ? "all" : "any",
  });
}

export function parseFindingsFiltersFromSearch(params: URLSearchParams): FindingsFilterUrlState {
  const projectRaw = params.get("project") ?? params.get("project_id");
  const projectParsed = projectRaw ? Number(projectRaw) : Number.NaN;
  const projectId = Number.isFinite(projectParsed) ? projectParsed : undefined;

  const active = (params.get("active") ?? "").toLowerCase();
  let status: FindingsFilterUrlState["status"] = "All";
  if (active === "true" || active === "1") {
    status = "Active";
  } else if (active === "false" || active === "0") {
    status = "Non-Active";
  } else {
    const fallbackStatus = (params.get("status") ?? "").toLowerCase();
    if (fallbackStatus === "active" || fallbackStatus === "enabled") {
      status = "Active";
    } else if (fallbackStatus === "non-active" || fallbackStatus === "disabled") {
      status = "Non-Active";
    }
  }

  const riskRaw: RiskState[] = [];
  const riskAccepted = (params.get("risk_accepted") ?? "").toLowerCase();
  const underReview = (params.get("under_review") ?? "").toLowerCase();
  const isMitigated = (params.get("is_mitigated") ?? "").toLowerCase();
  if (riskAccepted === "true" || riskAccepted === "1") riskRaw.push("risk_accepted");
  if (underReview === "true" || underReview === "1") riskRaw.push("under_review");
  if (isMitigated === "true" || isMitigated === "1") riskRaw.push("mitigated");

  return {
    projectId,
    pipelineId: params.get("pipeline") ?? params.get("pipeline_id") ?? undefined,
    title: params.get("title") ?? "",
    createdFrom: params.get("created_from") ?? params.get("created_gte") ?? "",
    createdTo: params.get("created_to") ?? params.get("created_lte") ?? "",
    statusUpdatedFrom:
      params.get("processed_from")
      ?? params.get("processed_gte")
      ?? params.get("status_updated_from")
      ?? params.get("status_updated_gte")
      ?? "",
    statusUpdatedTo:
      params.get("processed_to")
      ?? params.get("processed_lte")
      ?? params.get("status_updated_to")
      ?? params.get("status_updated_lte")
      ?? "",
    mitigatedFrom: params.get("mitigated_from") ?? params.get("mitigated_gte") ?? "",
    mitigatedTo: params.get("mitigated_to") ?? params.get("mitigated_lte") ?? "",
    workItemLinkedFrom: params.get("work_item_linked_gte") ?? "",
    workItemLinkedTo: params.get("work_item_linked_lte") ?? "",
    projectVersion: params.get("project_version") ?? "",
    file: params.get("file") ?? "",
    cwe: params.get("cwe") ?? "",
    severities: orderByPriority(parseCsv(params.get("severity")) as Severity[], SEVERITY_ORDER),
    tags: parseTagFilter(params),
    status,
    risk: orderByPriority(riskRaw, RISK_ORDER),
    aiStatus: params.get("ai_status") || "All",
    workItemStatus: _parseWorkItemStatus(params),
  };
}

export function buildFindingsFilterSearch(state: FindingsFilterUrlState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.projectId) params.set("project_id", String(state.projectId));
  if (state.pipelineId) params.set("pipeline_id", state.pipelineId);
  if (state.title) params.set("title", state.title);
  if (state.createdFrom) params.set("created_gte", state.createdFrom);
  if (state.createdTo) params.set("created_lte", state.createdTo);
  if (state.statusUpdatedFrom) params.set("processed_gte", state.statusUpdatedFrom);
  if (state.statusUpdatedTo) params.set("processed_lte", state.statusUpdatedTo);
  if (state.mitigatedFrom) params.set("mitigated_gte", state.mitigatedFrom);
  if (state.mitigatedTo) params.set("mitigated_lte", state.mitigatedTo);
  if (state.workItemLinkedFrom) params.set("work_item_linked_gte", state.workItemLinkedFrom);
  if (state.workItemLinkedTo) params.set("work_item_linked_lte", state.workItemLinkedTo);
  if (state.projectVersion) params.set("project_version", state.projectVersion);
  if (state.file) params.set("file", state.file);
  if (state.cwe) params.set("cwe", state.cwe);
  if (state.severities.length > 0) params.set("severity", orderByPriority(state.severities, SEVERITY_ORDER).join(","));
  const tags = normalizeTagFilter(state.tags);
  if (tags.include.length > 0) params.set(tags.matchMode === "all" ? "tags__and" : "tags", tags.include.join(","));
  if (tags.exclude.length > 0) params.set("not_tags", tags.exclude.join(","));
  if (state.status === "Active") params.set("active", "true");
  if (state.status === "Non-Active") params.set("active", "false");
  if (state.risk.includes("risk_accepted")) params.set("risk_accepted", "true");
  if (state.risk.includes("under_review")) params.set("under_review", "true");
  if (state.risk.includes("mitigated")) params.set("is_mitigated", "true");
  if (state.aiStatus && state.aiStatus !== "All") params.set("ai_status", state.aiStatus);
  if (state.workItemStatus && state.workItemStatus !== "all") params.set("work_item_status", state.workItemStatus);
  return params;
}

export function toFindingsApiFilters(
  state: FindingsFilterUrlState,
  options?: { limit?: number; offset?: number; ordering?: string },
): FindingFilters {
  return {
    projectId: state.projectId,
    pipelineId: state.pipelineId,
    title: state.title || undefined,
    createdGte: state.createdFrom || undefined,
    createdLte: state.createdTo || undefined,
    statusUpdatedGte: state.statusUpdatedFrom || undefined,
    statusUpdatedLte: state.statusUpdatedTo || undefined,
    processedGte: state.statusUpdatedFrom || undefined,
    processedLte: state.statusUpdatedTo || undefined,
    mitigatedGte: state.mitigatedFrom || undefined,
    mitigatedLte: state.mitigatedTo || undefined,
    workItemLinkedGte: state.workItemLinkedFrom || undefined,
    workItemLinkedLte: state.workItemLinkedTo || undefined,
    projectVersion: state.projectVersion || undefined,
    file: state.file || undefined,
    aiStatus:
      state.aiStatus === "All"
        ? undefined
        : (state.aiStatus as "has_ai" | "no_ai" | "ai_tp" | "ai_fp" | "ai_u"),
    workItemStatus: state.workItemStatus !== "all" ? state.workItemStatus : undefined,
    severities: state.severities.length ? state.severities : undefined,
    status: state.status === "Active" ? "enabled" : state.status === "Non-Active" ? "disabled" : undefined,
    riskStates: state.risk.length ? state.risk : undefined,
    cwe: state.cwe || undefined,
    tags: state.tags.include.length ? state.tags.include : undefined,
    tagMatchMode: state.tags.include.length ? state.tags.matchMode : undefined,
    excludedTags: state.tags.exclude.length ? state.tags.exclude : undefined,
    limit: options?.limit,
    offset: options?.offset,
    ordering: options?.ordering,
  };
}
